import React from "react";
import { 
  Box, 
  Typography, 
  Link, 
  Container,
  Grid,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery
} from "@mui/material";
import { 
  Email, 
  Phone, 
  LocationOn,
  LinkedIn,
  GitHub,
  Language
} from "@mui/icons-material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faEnvelope,
  faPhone,
  faMapMarkerAlt,
  faGlobe,
  faHeart
} from "@fortawesome/free-solid-svg-icons";

const Footer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const currentYear = new Date().getFullYear();

  return (
    <Box
      component="footer"
      sx={{
        background: 'linear-gradient(135deg, #002642 0%, #00577A 100%)',
        color: '#FFFFFF',
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        minHeight: 'auto',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          background: 'linear-gradient(135deg, rgba(219, 150, 0, 0.05) 0%, rgba(219, 150, 0, 0.02) 100%)',
          pointerEvents: 'none'
        }
      }}
    >
      {/* Decorative top border */}
      <Box
        sx={{
          height: '4px',
          background: 'linear-gradient(90deg, #DB9600 0%, #B8860B 50%, #DB9600 100%)',
          backgroundSize: '200% 100%',
          animation: 'shimmer 3s ease-in-out infinite',
          '@keyframes shimmer': {
            '0%': { backgroundPosition: '-200% 0' },
            '100%': { backgroundPosition: '200% 0' }
          }
        }}
      />

      <Box
        sx={{ 
          width: '100%',
          maxWidth: '100vw',
          py: { xs: 2, sm: 3, md: 4 },
          px: { xs: 1, sm: 2, md: 3, lg: 4, xl: 6 },
          position: 'relative',
          zIndex: 1,
          margin: '0 auto'
        }}
      >
        {isMobile ? (
          /* Vista Móvil - Layout Vertical */
          <Box sx={{ textAlign: 'center', width: '100%' }}>
            {/* Logo/Marca */}
            <Box sx={{ mb: 2.5, width: '100%' }}>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 700,
                  fontSize: { xs: '1.4rem', sm: '1.6rem' },
                  mb: 1,
                  background: 'linear-gradient(135deg, #FFFFFF 0%, #DB9600 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                }}
              >
                ComHub
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  fontWeight: 400,
                  px: { xs: 2, sm: 4 }
                }}
              >
                Soluciones inteligentes para copropiedades
              </Typography>
            </Box>

            {/* Contacto */}
            <Box sx={{ mb: 2.5, width: '100%' }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600,
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  mb: 2,
                  color: '#DB9600'
                }}
              >
                Contacto
              </Typography>
              
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 1.5, 
                alignItems: 'center',
                width: '100%'
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  width: '100%'
                }}>
                  <FontAwesomeIcon 
                    icon={faEnvelope} 
                    style={{ 
                      color: '#DB9600', 
                      fontSize: '1rem' 
                    }} 
                  />
                  <Link
                    href="mailto:comhub.development@gmail.com"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.9)',
                      textDecoration: 'none',
                      fontSize: { xs: '0.85rem', sm: '0.9rem' },
                      transition: 'all 0.3s ease',
                      wordBreak: 'break-all',
                      '&:hover': {
                        color: '#DB9600',
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    comhub.development@gmail.com
                  </Link>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FontAwesomeIcon 
                    icon={faGlobe} 
                    style={{ 
                      color: '#DB9600', 
                      fontSize: '1rem' 
                    }} 
                  />
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'rgba(255, 255, 255, 0.8)',
                      fontSize: { xs: '0.85rem', sm: '0.9rem' }
                    }}
                  >
                    Disponible 24/7
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Divider */}
            <Divider 
              sx={{ 
                borderColor: 'rgba(255, 255, 255, 0.1)', 
                mb: 2 
              }} 
            />

            {/* Copyright */}
            <Box>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '0.8rem',
                  mb: 1
                }}
              >
                © {currentYear} ComHub. Todos los derechos reservados.
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontSize: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5
                }}
              >
                Hecho con 
                <FontAwesomeIcon 
                  icon={faHeart} 
                  style={{ 
                    color: '#DB9600', 
                    fontSize: '0.7rem' 
                  }} 
                />
                para copropiedades inteligentes
              </Typography>
            </Box>
          </Box>
        ) : (
          /* Vista Escritorio - Layout en Grid */
          <Grid container spacing={3} alignItems="flex-start" sx={{ width: '100%' }}>
            {/* Columna 1: Marca y Descripción */}
            <Grid item xs={12} md={4} sx={{ width: '100%' }}>
              <Box>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 700,
                    fontSize: { sm: '1.5rem', md: '1.8rem', lg: '2rem' },
                    mb: 2,
                    background: 'linear-gradient(135deg, #FFFFFF 0%, #DB9600 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  ComHub
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.85)',
                    fontSize: { sm: '0.95rem', md: '1rem' },
                    lineHeight: 1.6,
                    mb: 2
                  }}
                >
                  Plataforma integral para la gestión inteligente de copropiedades, 
                  ofreciendo soluciones innovadoras para movilidad eléctrica, 
                  administración financiera y gestión energética.
                </Typography>
                
                {/* Iconos sociales/enlaces */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton 
                    sx={{ 
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&:hover': { 
                        color: '#DB9600',
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <Language />
                  </IconButton>
                  <IconButton 
                    sx={{ 
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&:hover': { 
                        color: '#DB9600',
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <GitHub />
                  </IconButton>
                  <IconButton 
                    sx={{ 
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&:hover': { 
                        color: '#DB9600',
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <LinkedIn />
                  </IconButton>
                </Box>
              </Box>
            </Grid>

            {/* Columna 2: Contacto */}
            <Grid item xs={12} md={4} sx={{ width: '100%' }}>
              <Box sx={{ width: '100%' }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    mb: 2,
                    color: '#DB9600'
                  }}
                >
                  Contacto & Soporte
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        p: 0.8,
                        borderRadius: 2,
                        backgroundColor: 'rgba(219, 150, 0, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <FontAwesomeIcon 
                        icon={faEnvelope} 
                        style={{ 
                          color: '#DB9600', 
                          fontSize: '0.9rem' 
                        }} 
                      />
                    </Box>
                    <Box>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: 'rgba(255, 255, 255, 0.7)',
                          fontSize: '0.8rem',
                          lineHeight: 1
                        }}
                      >
                        Email
                      </Typography>
                      <Link
                        href="mailto:comhub.development@gmail.com"
                        sx={{
                          color: 'rgba(255, 255, 255, 0.95)',
                          textDecoration: 'none',
                          fontSize: '0.9rem',
                          fontWeight: 500,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            color: '#DB9600',
                            textDecoration: 'underline'
                          }
                        }}
                      >
                        comhub.development@gmail.com
                      </Link>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        p: 0.8,
                        borderRadius: 2,
                        backgroundColor: 'rgba(219, 150, 0, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <FontAwesomeIcon 
                        icon={faGlobe} 
                        style={{ 
                          color: '#DB9600', 
                          fontSize: '0.9rem' 
                        }} 
                      />
                    </Box>
                    <Box>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: 'rgba(255, 255, 255, 0.7)',
                          fontSize: '0.8rem',
                          lineHeight: 1
                        }}
                      >
                        Disponibilidad
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: 'rgba(255, 255, 255, 0.95)',
                          fontSize: '0.9rem',
                          fontWeight: 500
                        }}
                      >
                        24/7 - Soporte técnico
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* Columna 3: Información Legal */}
            <Grid item xs={12} md={4} sx={{ width: '100%' }}>
              <Box sx={{ width: '100%' }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    mb: 2,
                    color: '#DB9600'
                  }}
                >
                  Información
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'rgba(255, 255, 255, 0.8)',
                      fontSize: '0.9rem',
                      lineHeight: 1.5
                    }}
                  >
                    Versión 1.0.0
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'rgba(255, 255, 255, 0.8)',
                      fontSize: '0.9rem',
                      lineHeight: 1.5
                    }}
                  >
                    Última actualización: Octubre 2025
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: '0.85rem',
                      lineHeight: 1.5
                    }}
                  >
                    © {currentYear} ComHub Development. 
                    <br />
                    Todos los derechos reservados.
                  </Typography>
                  
                  <Box sx={{ mt: 1 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'rgba(255, 255, 255, 0.6)',
                        fontSize: '0.8rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                      }}
                    >
                      Desarrollado con 
                      <FontAwesomeIcon 
                        icon={faHeart} 
                        style={{ 
                          color: '#DB9600', 
                          fontSize: '0.7rem' 
                        }} 
                      />
                      para copropiedades inteligentes
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default Footer;
